<script lang="ts" setup>
import { useMateTypeText, useTextImageUrl } from "@/use/common";
import { copyToClipboard, timeAgo } from "@/modules/common";
import { useTextImageModeText } from "@/enums/TextImageEnum";
import ReplyList from "@/components/mobile/reply/ReplyList.vue";
import { Reply, TextImage } from "@/types/entity";
import MomentActions from "@/components/mobile/popup/MomentActions.vue";
import { computed, ref, watch } from "vue";
import { ReplyInputInstance } from "@/components/mobile/reply/ReplyInput.vue";
import { useWindowSize } from "@vueuse/core";
import { goMateProfile } from "@/use/go";

const props = defineProps<{
  target: TextImage;
}>();
const emit = defineEmits<{
  (e: "showShare"): void;
}>();

// States
const replyTarget = ref<Reply | null>(props.target.reply);
const replyInputRef = ref<ReplyInputInstance | null>(null);
const toOperateReply = ref<Reply | null>(null);
const showReplyAction = ref(false);
const { width } = useWindowSize();
const isSmallScreen = computed(() => width.value <= 1024);

// Lifecycle
watch(
  () => props.target,
  () => {
    replyTarget.value = props.target.reply;
  }
);

// Event Handler
const onReplyBtnClick = (replay: Reply) => {
  replyTarget.value = replay;
  replyInputRef.value?.focus();
};
const onReplyActionClick = (reply: Reply) => {
  toOperateReply.value = reply;
  showReplyAction.value = true;
};
</script>
<template>
  <div
    class="flex flex-col p-4 gap-4 overflow-x-hidden overflow-y-auto no-scrollbar flex-grow"
  >
    <div class="flex justify-between items-center">
      <div
        class="flex gap-2 cursor-pointer"
        @click="goMateProfile(target.mate.uuid)"
      >
        <UserAvatar :avatar-url="target.mate.mediaUrl" class="h-12" />
        <div class="flex flex-col gap-2">
          <span>{{ target.mate.name }}</span>
          <span class="text-base-content/50">
            {{ useMateTypeText(target.mate.type) }}
          </span>
        </div>
      </div>
      <button
        class="btn btn-xs md:btn-sm btn-ghost btn-square"
        @click="emit('showShare')"
      >
        <i class="iconify mingcute--share-2-line" />
      </button>
    </div>

    <div class="text-base-content/50">{{ timeAgo(target.dtCreate) }}</div>
    <div class="text-xs bg-base-300 p-4 rounded-lg flex flex-col gap-4">
      <div class="flex justify-between">
        <div class="flex flex-col gap-2">
          <div class="text-base-content/50">{{ $t("Message.type") }}</div>
          <div class="badge badge-neutral text-neutral-content badge-sm">
            {{ useTextImageModeText(target.mode) }}
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <!--                <div class="text-base-content/50">-->
          <!--                  {{ $t("painting.proportion") }}-->
          <!--                </div>-->
          <!--                <div>{{ target.ratio }}</div>-->
        </div>
      </div>
      <div class="flex justify-between">
        <div
          v-if="target?.media?.width && target.media.height"
          class="flex flex-col gap-2"
        >
          <div class="text-base-content/50">{{ $t("Image.size") }}</div>
          <div>
            {{ `${target.media.width}x${target.media.height}` }}
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <div class="text-base-content/50">
            {{ $t("painting.proportion") }}
          </div>
          <div>{{ target.ratio }}</div>
        </div>
      </div>
    </div>
    <div>
      <StickyNav
        v-if="!isSmallScreen"
        :class-name="'bg-[rgba(13,14,18)]'"
        :show-back="false"
      >
        <div class="p-2 text-lg font-bold flex items-center gap-2">
          <i class="iconify mingcute--comment-line" />
          {{ $t("Message.discussion") }}
        </div>
      </StickyNav>
      <div v-else class="p-2 text-lg font-bold flex items-center gap-2">
        <i class="iconify mingcute--comment-line" />
        {{ $t("Message.discussion") }}
      </div>
      <div class="h-fit">
        <ReplyInput
          ref="replyInputRef"
          :reply-target="replyTarget"
          :show-tip="target.reply.id !== replyTarget?.id"
          class="bg-transparent border"
          @reply-aborted="replyTarget = target.reply"
        />
      </div>
      <div class="p-2">
        <reply-list
          :force-re-fetch="false"
          :root-id="target?.reply.id"
          @on-reply-click="onReplyBtnClick"
          @on-reaction-click="onReplyActionClick"
        />
      </div>
    </div>
    <moment-actions v-model:show="showReplyAction" :reply="toOperateReply" />
  </div>
</template>
